import { useState } from "react";

import { Meta, UppyFile } from "@uppy/core";

import { Uploader, UploaderProps } from "@/components/pieces/uploader";
import { Button } from "@/components/ui/button";

interface CharacterPhotoSectionProps {
  photoUrl: string | undefined;
  photoEndpoint: string;
  onPhotoUpload: UploaderProps["onUploadSuccess"];
  onChoosePhoto: () => void;
}

export function CharacterPhotoSection({
  photoUrl,
  photoEndpoint,
  onPhotoUpload,
  onChoosePhoto,
}: CharacterPhotoSectionProps) {
  const [showUploader, setShowUploader] = useState(false);

  const handlePhotoUpload: UploaderProps["onUploadSuccess"] = async (
    file: UppyFile<Meta, Record<string, never>> | undefined,
    response: {
      body?: Record<string, unknown>;
      status: number;
      bytesUploaded?: number;
      uploadURL?: string;
    },
  ) => {
    await onPhotoUpload?.(file, response);
    setShowUploader(false);
  };

  if (showUploader) {
    return (
      <div className="flex flex-col gap-3 mb-4">
        <Button
          size="sm"
          variant="solid"
          intent="secondary"
          onClick={() => setShowUploader(false)}
        >
          Keep current photo
        </Button>
        <Uploader
          endpoint={photoEndpoint}
          fieldName="photo"
          onComplete={() => setShowUploader(false)}
          onUploadSuccess={handlePhotoUpload}
        />
      </div>
    );
  }

  return (
    <div className="flex items-start gap-5 mb-6">
      {/* Avatar image */}
      <img
        src={photoUrl || "/default-avatar.png"}
        alt="Character"
        className="w-20 h-20 rounded-full object-cover"
      />

      {/* Buttons and description */}
      <div className="flex flex-col gap-2 flex-1">
        <div className="flex flex-col sm:flex-row gap-2">
          <Button
            variant="solid"
            intent="secondary"
            onClick={onChoosePhoto}
            className="w-full sm:w-auto"
          >
            Choose Photo
          </Button>
          <Button
            variant="solid"
            intent="secondary"
            onClick={() => setShowUploader(true)}
            className="w-full sm:w-auto"
          >
            Upload Photo
          </Button>
        </div>
        <p className="text-sm text-gray-500">
          Square images at least 150×150px
        </p>
      </div>
    </div>
  );
}
